import { useState } from "react";
import { Quote, CheckCircle2, BadgeCheck, Milestone, GraduationCap, HeartPulse, BookOpen, Sprout } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Reveal from "@/components/Reveal";

const FILTERS = [
  { key: "all", label: "All Sectors", testId: "case-filter-all" },
  { key: "education", label: "Education & Skill", testId: "case-filter-education" },
  { key: "healthcare", label: "Healthcare & Nutrition", testId: "case-filter-healthcare" },
  { key: "rural", label: "Rural & Women Empowerment", testId: "case-filter-rural" },
];

const CASES = [
  {
    id: 1,
    sector: "education",
    name: "Digital Literacy Mission",
    partner: "Tier-1 IT Company CSR Desk",
    banner: "from-[#0E2A6B] to-[#2E6BE6]",
    icon: GraduationCap,
    summary: "Digital literacy program for first-generation learners in government schools.",
    deliverables: [
      "CSR-1 registration completed for the NGO",
      "Pitch deck restructured around learning-outcome metrics",
      "MoU with milestone-linked tranche disbursement",
      "Renewed for a second year on performance",
    ],
    quote:
      "They turned our scattered documents into a dossier the CSR head called one of the cleanest proposals they had received.",
    quoteBy: "Program Director, Education NGO",
  },
  {
    id: 2,
    sector: "healthcare",
    name: "Community Health Drive",
    partner: "Healthcare CSR Consortium",
    banner: "from-[#C96F1B] to-[#E8862A]",
    icon: HeartPulse,
    summary: "Community health & nutrition outreach across rural villages.",
    deliverables: [
      "Consortium structure designed so multiple corporates pooled budgets",
      "80G / 12A audit gaps closed before due diligence",
      "Baseline health metrics framework for M&E reporting",
      "Quarterly utilization certificates systematized",
    ],
    quote:
      "We had been turned away by corporates for years. With Impact Sutra, we signed our first consortium grant within months.",
    quoteBy: "Managing Trustee, Health Trust",
  },
  {
    id: 3,
    sector: "education",
    name: "Smart Classrooms Program",
    partner: "Public Sector Energy Major",
    banner: "from-[#2E6BE6] to-[#7AA5F0]",
    icon: BookOpen,
    summary: "Smart classroom infrastructure for government schools across multiple states.",
    deliverables: [
      "Geo-tagged infrastructure mapping for the proposal",
      "Vendor procurement policy aligned to CSR audit norms",
      "Phased rollout plan tied to tranche releases",
      "Impact documentation prepared for the funder's annual report",
    ],
    quote:
      "Their boardroom representation meant our small team never had to leave the field. The grant process ran itself.",
    quoteBy: "Founder, Education Initiative",
  },
  {
    id: 4,
    sector: "rural",
    name: "Women's Livelihood Collective",
    partner: "Private Bank CSR Program",
    banner: "from-[#1F7A33] to-[#2E9E44]",
    icon: Sprout,
    summary: "Livelihood & financial inclusion program for rural women entrepreneurs.",
    deliverables: [
      "SHG federation documents structured for due diligence",
      "Livelihood income-tracking dashboard for M&E",
      "Pitch positioned under the funder's women-empowerment mandate",
      "Follow-on tranche supported by first-year outcomes",
    ],
    quote:
      "They understood both our field reality and the funder's compliance language. That bridge is everything.",
    quoteBy: "Secretary, Women's Collective",
  },
];

export default function CaseStudies() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const visible = CASES.filter((c) => filter === "all" || c.sector === filter);

  return (
    <section id="work" className="bg-[#F4F3EE] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="font-mono-alt mb-4 text-xs font-semibold tracking-[0.35em] text-[#E8862A]">03 / ENGAGEMENT PLAYBOOKS</p>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C7DAF5] bg-[#EAF1FC] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#0E2A6B]">
              Illustrative Scenarios
            </span>
            <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              How CSR engagements{" "}
              <span className="font-display italic text-[#0E2A6B]">typically unfold with us</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Representative scenarios drawn from the kinds of CSR mandates we support. Organization
              names and specifics are generalized — real references are shared during consultation.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={f.testId}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                  filter === f.key
                    ? "bg-[#0E2A6B] text-white shadow-md"
                    : "border border-[#E6E9E6] bg-white text-slate-600 hover:border-[#3D63B8]/40 hover:text-[#0E2A6B]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <Reveal className="mt-12 grid gap-6 sm:grid-cols-2" delay={0.1}>
          {visible.map((c) => (
            <article
              key={c.id}
              data-testid={`case-study-card-${c.id}`}
              className="group overflow-hidden rounded-3xl border border-[#E6E9E6] bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              <div className={`relative h-36 overflow-hidden bg-gradient-to-br ${c.banner}`}>
                <c.icon className="absolute -bottom-5 -right-5 h-32 w-32 text-white/15" strokeWidth={1} />
                <svg className="absolute left-6 top-6 opacity-50" width="120" height="70" viewBox="0 0 120 70" fill="none">
                  <path d="M4 62 C 40 50, 75 32, 112 8" stroke="white" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="30" cy="54" r="5" fill="white" fillOpacity="0.8" />
                  <circle cx="104" cy="12" r="5" fill="white" fillOpacity="0.8" />
                </svg>
                <span className="absolute left-4 top-4 rounded-full bg-[#0A1A3F]/85 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                  {FILTERS.find((f) => f.key === c.sector)?.label}
                </span>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-lg font-bold text-slate-900">{c.name}</h3>
                  <span className="inline-flex items-center gap-1 rounded-lg bg-[#EAF1FC] px-2.5 py-1 text-xs font-semibold text-[#0E2A6B]">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Grant Secured
                  </span>
                </div>
                <p className="mt-1 text-xs font-medium uppercase tracking-wider text-[#E8862A]">
                  {c.partner}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.summary}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    <Milestone className="h-3.5 w-3.5" />
                    Full mandate lifecycle
                  </span>
                  <button
                    data-testid={`btn-view-sample-dossier-${c.id}`}
                    onClick={() => setActive(c)}
                    className="text-sm font-semibold text-[#0E2A6B] underline-offset-4 transition-colors hover:text-[#E8862A] hover:underline"
                  >
                    View full dossier →
                  </button>
                </div>
              </div>
            </article>
          ))}
        </Reveal>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-slate-900">
                  {active.name} — CSR Grant Mandate
                </DialogTitle>
              </DialogHeader>
              <div className={`relative mt-2 flex h-40 items-center justify-center rounded-2xl bg-gradient-to-br ${active.banner}`}>
                <active.icon className="h-16 w-16 text-white/40" strokeWidth={1} />
              </div>
              <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <p className="rounded-xl bg-[#F4F3EE] p-3">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Corporate Partner</span>
                  <span className="font-semibold text-slate-900">{active.partner}</span>
                </p>
                <p className="rounded-xl bg-[#F4F3EE] p-3">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Engagement</span>
                  <span className="font-semibold text-slate-900">Audit → Pitch → Disbursement → Renewal</span>
                </p>
              </div>
              <div className="mt-5">
                <p className="text-sm font-semibold text-slate-900">Key deliverables</p>
                <ul className="mt-2 space-y-2">
                  {active.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0E2A6B]" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
              <blockquote className="mt-5 rounded-2xl border-l-4 border-[#E8862A] bg-[#FDF1E3] p-4">
                <Quote className="h-4 w-4 text-[#E8862A]" />
                <p className="mt-2 font-display text-sm italic text-slate-800">"{active.quote}"</p>
                <footer className="mt-2 text-xs font-semibold text-slate-500">— {active.quoteBy}</footer>
              </blockquote>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
